<template>
  <div class="p-1">
    <!-- Search section -->
    <div class="w-100">
      <cart-search-save-clear-section
        :cart-list="cartList"
        :is-saving-cart="isSavingCart || isClearingCart"
        @on-search-cart-products="onSearchCartProducts"
        @on-click-save-cart="onClickSaveCart"
        @on-click-clear-cart="handleClearCart"
        @on-selected-store="onSelectedStore"
      />
    </div>
    <!-- Cart list loop -->
    <div class="w-100">
      <div
        v-for="(cart, index) in filteredCartList"
        :key="'cart' + cart._id"
      >
        <template v-if="!!cart.products.length">
          <hr>
          <cart-v2
            :cart="cart"
            @on-updated-cart-values="onUpdatedCartValues($event, index)"
          />
        </template>
      </div>
    </div>
    <clear-carts-modal
      @on-close-modal="trackClearCartModal('close', cartList)"
      @on-click-clear-cart="onClickClearCart"
    />
  </div>
</template>

<script>
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { DELETE_CARTS, UPDATE_CARTS } from '@/store/modules/checkout-v2.module'
import { GET_COMMON_BASE } from '@/store/modules/common.module'
import analytics from '@/@core/utils/analytics'
import constants, { CART_PRODUCTS_SEARCH_FIELDS, CART_PRODUCTS_SEARCH_ITEMS_FIELDS } from '@/constants'
import { searchProductUsingFields } from '@/@core/utils/utils'
import {
  buildCartSearchIndex,
  filterProductsByIndex,
  CART_SEARCH_INDEX_THRESHOLD,
} from '@/@core/utils/cart-search-index'
import _ from 'lodash'
import CartSearchSaveClearSection from './CartSearchSaveClearSection.vue'
import CartV2 from './CartV2.vue'
import ClearCartsModal from './components/ClearCartsModal.vue'
import { trackClearCartModal } from './trackMultipleStoreCartHelpers'

const cartErrorMessage = cart => (cart?.storeName ? `${cart.storeName} - ${cart.error}` : cart?.error)

function shouldIndexCart(cart) {
  if (!cart || !cart._id) return false
  if (!Array.isArray(cart.products)) return false
  return cart.products.length >= CART_SEARCH_INDEX_THRESHOLD
}

function toastCartErrors(err) {
  const failedCarts = err?.response?.data?.data?.failedCarts ?? []
  if (!failedCarts.length) return apiToastError(err)
  failedCarts.forEach(cart => apiToastError(cartErrorMessage(cart)))
}

const CART_WITH_MULTIPLE_STORES = constants.TRACKS.ACTIONS.CART_WITH_MULTIPLE_STORES
const searchFields = CART_PRODUCTS_SEARCH_FIELDS
const itemFields = CART_PRODUCTS_SEARCH_ITEMS_FIELDS

export default {
  name: 'CartList',
  components: {
    CartV2,
    CartSearchSaveClearSection,
    ClearCartsModal,
  },
  props: {
    cartList: {
      type: Array,
      required: true,
    },
    loadCarts: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      cartValuesList: [],
      filteredCartList: [],
      isSavingCart: false,
      isClearingCart: false,
      // Per-cart lowercased token indexes keyed by cart._id. Built once per
      // cartList change so search filtering is a single `indexOf` per
      // product instead of walking 9 fields per product per keystroke.
      cartSearchIndexes: Object.create(null),
      currentStoreFilter: null,
      currentSearchText: '',
      trackClearCartModal,
    }
  },
  emits: ['get-cart-values-list'],
  watch: {
    cartList: {
      handler(newList) {
        // When the store-level cart list changes (cart loaded, cart saved,
        // product added/removed) rebuild the indexes and re-apply any
        // currently active filters so the displayed list stays consistent.
        this.rebuildSearchIndexes(newList)
        this.applyFilters()
      },
    },
  },
  created() {
    this.$store.dispatch(GET_COMMON_BASE)
    this.filteredCartList = this.cartList
    this.rebuildSearchIndexes(this.cartList)
  },
  methods: {
    handleClearCart() {
      this.$bvModal.show('clear_carts')
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_CLEAR_CART_BUTTON, { carts: this.cartValuesList })
    },
    rebuildSearchIndexes(cartList) {
      const next = Object.create(null)
      cartList.forEach(cart => {
        if (shouldIndexCart(cart)) {
          next[cart._id] = buildCartSearchIndex(cart.products)
        }
      })
      this.cartSearchIndexes = next
    },
    filterCartProducts(cart, searchText) {
      if (!searchText) return cart.products
      const index = this.cartSearchIndexes[cart._id]
      if (index) return filterProductsByIndex(cart.products, index, searchText)
      return cart.products.filter(product => searchProductUsingFields(
        product, searchFields, searchText, itemFields,
      ))
    },
    applyFilters() {
      const storeId = this.currentStoreFilter
      const searchText = this.currentSearchText
      const scopedList = storeId
        ? this.cartList.filter(cart => cart._id === storeId)
        : this.cartList
      this.filteredCartList = scopedList.map(cart => ({
        ...cart,
        products: this.filterCartProducts(cart, searchText),
      }))
    },
    onSelectedStore(storeId) {
      this.currentStoreFilter = storeId || null
      this.applyFilters()
    },
    onSearchCartProducts: _.debounce(function onSearchCartProducts(val) {
      this.onSearchCartProductsByText(val)
    }, 300),
    onSearchCartProductsByText(searchText = '') {
      this.currentSearchText = searchText
      this.applyFilters()
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_SEARCH_PRODUCTS_IN_CART, { searchText })
    },
    async onClickSaveCart() {
      analytics.track(CART_WITH_MULTIPLE_STORES.RETAILER_CLICKED_SAVE_CART_BUTTON, { carts: this.cartValuesList })
      this.isSavingCart = true
      try {
        const res = await this.$store.dispatch(UPDATE_CARTS, { params: { isUpdateFromCartPage: true, skipMinQtyValidation: true } })
        apiToastSuccess(res.data.message)
        analytics.track(CART_WITH_MULTIPLE_STORES.CART_SAVED_SUCCESSFULLY, { carts: this.cartValuesList })
      } catch (err) {
        toastCartErrors(err)
        analytics.track(CART_WITH_MULTIPLE_STORES.GETTING_ERROR_WHILE_SAVING_CART, err?.response)
      } finally {
        this.isSavingCart = false
      }
    },
    onClickClearCart() {
      this.$bvModal.hide('clear_carts')
      trackClearCartModal('confirm', this.cartList)
      this.isClearingCart = true
      const storeIds = this.filteredCartList.map(cart => cart.userId)
      this.$store.dispatch(DELETE_CARTS, storeIds).then(res => {
        apiToastSuccess(res.data.message)
        this.isClearingCart = false
        this.loadCarts()
        trackClearCartModal('success', this.cartList, { response: res })
      })
      .catch(err => {
        apiToastError(err)
        this.isClearingCart = false
        trackClearCartModal('failed', this.cartList, { error: err })
      })
    },
    onUpdatedCartValues(cartValues, index) {
      this.cartValuesList[index] = cartValues
      this.$emit('get-cart-values-list', this.cartValuesList)
    },
  },
}
</script>
